import { useCallback, useEffect, useState } from 'react';
import { Button, InfoBox, StatusBadge, Textarea, type StatusBadgeTone } from '@immo/ui-kit';
import {
  FLEET_JOB_MESSAGE_MAX_LENGTH,
  type FleetJobMessageDelivery,
  type FleetJobMessageView,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { useLocaleFormat } from '@/i18n/use-locale-format';

/** Re-read while a message is still on its way, so the delivery steps move without a page poll. */
const PENDING_POLL_MS = 2_000;

const DELIVERY_TONES: Record<FleetJobMessageDelivery, StatusBadgeTone> = {
  waiting: 'neutral',
  sent: 'info',
  received: 'info',
  delivered: 'success',
  'not-delivered': 'danger',
};

function errorCodeOf(body: unknown): string | undefined {
  return (body as { error?: { code?: string } } | undefined)?.error?.code;
}

type Props = {
  readonly jobId: string;
  readonly canCommand: boolean;
};

/**
 * Steering a running job (OPS-55) — what replying in its Buzz thread used to be. A message that is just `stop` ends
 * the round at its next safe point; anything else is an instruction the round reads after its next tool call. Each
 * message shows who sent it, when, and how far it got: waiting for the host, sent, received, delivered into the
 * round's inbox, or not delivered (with the host's reason).
 */
export function JobMessages({ jobId, canCommand }: Props) {
  const t = useTranslations('pages.admin.jobs');
  const tErrors = useTranslations('errors');
  const format = useLocaleFormat();
  const [messages, setMessages] = useState<ReadonlyArray<FleetJobMessageView>>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const result = await apiClient.adminFleetJobs.listMessages({ pathParams: { jobId } });

    if (result.ok) setMessages(result.body.data);
  }, [jobId]);

  // The thread belongs to the selected job: dropped and re-read whenever the selection moves.
  useEffect(() => {
    setMessages([]);
    setDraft('');
    setError(null);
    void load();
  }, [load]);

  const pending = messages.some((message) => message.delivery !== 'delivered' && message.delivery !== 'not-delivered');

  useEffect(() => {
    if (!pending) return;

    const interval = window.setInterval(() => void load(), PENDING_POLL_MS);

    return () => window.clearInterval(interval);
  }, [pending, load]);

  const send = async (text: string) => {
    setBusy(true);
    setError(null);

    try {
      const result = await apiClient.adminFleetJobs.sendMessage({ pathParams: { jobId }, body: { text } });

      if (result.ok) {
        setDraft('');
        setMessages((previous) => [...previous, result.body]);

        return;
      }

      const code = errorCodeOf(result.body);
      setError(code !== undefined ? tErrors(code) : t('generic-error'));
    } finally {
      setBusy(false);
    }
  };

  const stopRequest = messages.find((message) => message.stop) ?? null;

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-sm font-semibold text-text-primary">{t('messages-heading')}</h2>

      {stopRequest !== null && (
        <InfoBox type="warning">
          {t('stop-requested-note', { name: stopRequest.authorName, time: format.moment(stopRequest.sentAt) })}
        </InfoBox>
      )}

      {messages.length === 0 ? (
        <p className="text-sm text-text-tertiary">{t('messages-empty')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {messages.map((message) => (
            <li key={message.id} className="rounded-md border border-border bg-surface p-3">
              <div className="flex flex-wrap items-center gap-2">
                {message.stop && <StatusBadge label={t('message-stop-badge')} tone="warning" size="sm" />}
                <span className="min-w-0 flex-1 whitespace-pre-wrap break-words text-sm text-text-primary">
                  {message.text}
                </span>
                <StatusBadge
                  label={t(`message-delivery.${message.delivery}`)}
                  tone={DELIVERY_TONES[message.delivery]}
                  size="sm"
                />
              </div>
              <div className="mt-1 text-xs text-text-tertiary">
                {t('message-meta', { name: message.authorName, time: format.moment(message.sentAt) })}
              </div>
              {message.delivery === 'not-delivered' && message.detail !== null && (
                <div className="mt-1 break-words text-xs text-text-secondary">{message.detail}</div>
              )}
            </li>
          ))}
        </ul>
      )}

      {canCommand && (
        <div className="flex flex-col gap-2">
          <Textarea
            aria-label={t('message-input')}
            maxLength={FLEET_JOB_MESSAGE_MAX_LENGTH}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            disabled={busy}
          />
          <p className="text-xs text-text-tertiary">{t('message-stop-hint')}</p>
          {error !== null && (
            <p role="alert" className="text-sm text-danger-600">
              {error}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={() => void send(draft.trim())} disabled={busy || draft.trim().length === 0}>
              {t('message-send-cta')}
            </Button>
            <Button type="button" variant="danger" onClick={() => void send('stop')} disabled={busy}>
              {t('message-stop-cta')}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
